import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AnnoncenewService } from '../service/annoncenew.service';
import { Annonce, AnnonceFormData } from '../models/annonce.model';

@Component({
  selector: 'app-updateannonce',
  templateUrl: './updateannonce.component.html',
  styleUrls: ['./updateannonce.component.scss']
})
export class UpdateannonceComponent implements OnInit {
  annonceForm: FormGroup;
  isLoading = false;
  isSubmitting = false;
  annonceId!: number;
  annonce!: Annonce;

  villes = [
    'Tunis', 'Ariana', 'Ben Arous', 'Manouba', 'Nabeul', 'Zaghouan',
    'Bizerte', 'Béja', 'Jendouba', 'Kef', 'Siliana', 'Sousse',
    'Monastir', 'Mahdia', 'Sfax', 'Kairouan', 'Kasserine',
    'Sidi Bouzid', 'Gabès', 'Médenine', 'Tataouine', 'Gafsa',
    'Tozeur', 'Kébili'
  ];

  typeOptions = [
    { value: 'Vente', label: 'Vente' },
    { value: 'Location', label: 'Location' },
    { value: 'Location Vacances', label: 'Location Vacances' }
  ];

  annonceTypes = [
    { value: 'Appartements', label: 'Appartements' },
    { value: 'Bureaux', label: 'Bureaux' },
    { value: 'Commerces', label: 'Commerces' },
    { value: 'Duplexes', label: 'Duplexes' },
    { value: 'Industriel', label: 'Industriel' },
    { value: 'Lots', label: 'Lots' },
    { value: 'Studios', label: 'Studios' },
    { value: 'Terrain agricole', label: 'Terrain agricole' },
    { value: 'Villas', label: 'Villas / maisons' }
  ];

  constructor(
    private fb: FormBuilder,
    private annonceService: AnnoncenewService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar
  ) {
    this.annonceForm = this.fb.group({
      annonceName: ['', [Validators.required, Validators.minLength(3)]],
      adresse: ['', [Validators.required, Validators.minLength(5)]],
      ville: ['', Validators.required],
      typeAnnonce: ['', Validators.required],
      description: ['', [Validators.required, Validators.minLength(20)]],
      surface: ['', [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      prix: ['', [Validators.required]],
      phone1: ['', [Validators.required]],
      phone2: [''],
      email: ['', [Validators.email]],
      urlGoogleMap: [''],
      urlYoutube: [''],
      photoPrincipale: ['', Validators.required],
      imagesList: [''],
      isApproved: [false],
      viewCount: [0]
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.annonceId = +params['id'];
      this.loadAnnonce();
    });
  }

  loadAnnonce(): void {
    this.isLoading = true;
    this.annonceService.getAnnonceById(this.annonceId).subscribe({
      next: (annonce: Annonce) => {
        this.annonce = annonce;
        this.populateForm(annonce);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading annonce:', error);
        this.snackBar.open('Erreur lors du chargement de l\'annonce', 'Fermer', {
          duration: 3000,
          panelClass: ['error-snackbar']
        });
        this.isLoading = false;
        this.router.navigate(['/annoncenew']);
      }
    });
  }

  populateForm(annonce: Annonce): void {
    this.annonceForm.patchValue({
      annonceName: annonce.annonceName,
      adresse: annonce.adresse,
      ville: annonce.ville,
      typeAnnonce: annonce.typeAnnonce,
      description: annonce.description || '',
      surface: annonce.surface || '',
      prix: annonce.prix,
      phone1: annonce.phone1,
      phone2: annonce.phone2 || '',
      email: annonce.email || '',
      urlGoogleMap: annonce.urlGoogleMap || '',
      urlYoutube: annonce.urlYoutube || '',
      photoPrincipale: annonce.photoPrincipale || '',
      imagesList: annonce.imagesList || '',
      isApproved: annonce.isApproved || false,
      viewCount: annonce.viewCount || 0
    });
  }

  get formControls() {
    return this.annonceForm.controls;
  }

  onSubmit(): void {
    if (this.annonceForm.invalid) {
      this.markFormGroupTouched(this.annonceForm);
      this.snackBar.open('Veuillez remplir tous les champs obligatoires correctement', 'Fermer', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.isSubmitting = true;
    
    const formData: AnnonceFormData = {
      annonceName: this.annonceForm.value.annonceName,
      adresse: this.annonceForm.value.adresse,
      ville: this.annonceForm.value.ville,
      typeAnnonce: this.annonceForm.value.typeAnnonce,
      description: this.annonceForm.value.description,
      surface: this.annonceForm.value.surface,
      prix: this.annonceForm.value.prix,
      phone1: this.annonceForm.value.phone1,
      phone2: this.annonceForm.value.phone2 || undefined,
      email: this.annonceForm.value.email || undefined,
      urlGoogleMap: this.annonceForm.value.urlGoogleMap || undefined,
      urlYoutube: this.annonceForm.value.urlYoutube || undefined,
      photoPrincipale: this.annonceForm.value.photoPrincipale,
      imagesList: this.annonceForm.value.imagesList || undefined
    };

    this.annonceService.updateAnnonce(this.annonceId, formData).subscribe({
      next: (response) => {
        this.snackBar.open('Annonce modifiée avec succès!', 'Fermer', {
          duration: 3000,
          panelClass: ['success-snackbar']
        });
        this.router.navigate(['/annoncenew']);
      },
      error: (error) => {
        console.error('Error updating annonce:', error);
        this.snackBar.open('Erreur lors de la modification de l\'annonce', 'Fermer', {
          duration: 3000,
          panelClass: ['error-snackbar']
        });
        this.isSubmitting = false;
      },
      complete: () => {
        this.isSubmitting = false;
      }
    });
  }

  onCancel(): void {
    if (this.annonceForm.dirty) {
      const confirmLeave = confirm('Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter?');
      if (confirmLeave) {
        this.router.navigate(['/annoncenew']);
      }
    } else {
      this.router.navigate(['/annoncenew']);
    }
  }

  onDelete(): void {
    const confirmed = confirm(`Êtes-vous sûr de vouloir supprimer l'annonce "${this.annonce?.annonceName}"? Cette action est irréversible.`);
    
    if (confirmed) {
      this.isLoading = true;
      this.annonceService.deleteAnnonce(this.annonceId).subscribe({
        next: () => {
          this.snackBar.open('Annonce supprimée avec succès!', 'Fermer', {
            duration: 3000,
            panelClass: ['success-snackbar']
          });
          this.router.navigate(['/annoncenew']);
        },
        error: (error) => {
          console.error('Error deleting annonce:', error);
          this.snackBar.open('Erreur lors de la suppression de l\'annonce', 'Fermer', {
            duration: 3000,
            panelClass: ['error-snackbar']
          });
          this.isLoading = false;
        }
      });
    }
  }

  markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  formatImagesList(event: any): void {
    const value = event.target.value;
    if (value) {
      // Split by commas and newlines, filter empty strings, join with commas
      const imagesArray = value.split(/[,\n]+/).filter((img: string) => img.trim() !== '');
      this.annonceForm.patchValue({ imagesList: imagesArray.join(',') });
    }
  }

  getImagesCount(): number {
    const imagesList = this.annonceForm.get('imagesList')?.value;
    if (!imagesList) return 0;
    return imagesList.split(',').filter((img: string) => img.trim() !== '').length;
  }

  previewImage(): string {
    const mainImage = this.annonceForm.get('photoPrincipale')?.value;
    return mainImage || 'assets/images/no-image.jpg';
  }

  getStatusBadge(status: boolean | undefined): string {
    return status ? 'Approuvé' : 'En attente';
  }

  getStatusIcon(isApproved: boolean | undefined): string {
    return isApproved ? 'check_circle' : 'pending';
  }

  toggleApproval(): void {
    const newStatus = !this.annonceForm.get('isApproved')?.value;
    this.annonceForm.patchValue({ isApproved: newStatus });
  }
}